/**
 * Recipes: how the lines are numbered, how one of them is changed, and who may.
 *
 * Pure logic, like everything else in this folder — no React, no storage, no
 * model. The editor, the reading page, the assistant's prompt and the tests all
 * read the same few functions, which is what keeps "line 5" meaning the same
 * thing on the screen, in what is sent to the model, and in what comes back.
 *
 * ## Why the lines are numbered at all
 *
 * A recipe is corrected by pointing. Somebody reads what was written up from
 * their telling, spots that the sugar is wrong, and says so — out loud, to a
 * microphone — and "the sugar one, no, the second sugar one" is not something a
 * person should have to say or a model should have to guess at. A number down
 * the side of the page turns it into "line five should be two cups", which is
 * unambiguous to both of them.
 *
 * So the numbering has to be **boring**: the title is always line 1, then the
 * page in reading order, one number per line of words, no gaps, nothing numbered
 * that is not words somebody could change. The section labels ("Ingredients",
 * "Method") are furniture and carry no number — they are drawn by the page, in
 * the reader's own language, and cannot be broken by an edit.
 */
import { addedTo } from './milestones';
import { managesPerson, type Actor } from './permissions';
import type { FamilyPhoto, Lang, Recipe, RecipeDoc, UUID } from './types';

// ---------------------------------------------------------------------------
// Limits — generous for a recipe, and a ceiling on what one request can cost
// ---------------------------------------------------------------------------

export const MAX_TITLE_CHARS = 140;
export const MAX_LINE_CHARS = 600;
/** Per section. A wedding-sized couscous is forty lines; nothing real is eighty. */
export const MAX_SECTION_LINES = 80;
/** A long telling is a couple of thousand words. Past this it is not a recipe. */
export const MAX_SOURCE_CHARS = 12_000;

// ---------------------------------------------------------------------------
// The page, as numbered lines
// ---------------------------------------------------------------------------

export type RecipeSection = 'title' | 'intro' | 'facts' | 'ingredients' | 'steps' | 'notes';
/** The sections that are lists — the ones a line can be added to or taken from. */
export type RecipeListSection = 'facts' | 'ingredients' | 'steps' | 'notes';

export const LIST_SECTIONS: readonly RecipeListSection[] = ['facts', 'ingredients', 'steps', 'notes'];

export interface RecipeLine {
  /** Its number on the page, from 1 — what somebody says out loud: "line five". */
  n: number;
  section: RecipeSection;
  /** Where it sits within its section (0 for the title and the line about the dish). */
  index: number;
  text: string;
  /** A sub-heading — "For the syrup:" — rather than an ingredient or a step. */
  heading: boolean;
  /** For a real step, which one: "step 3" counts the steps and skips their headings. */
  step?: number;
}

export function emptyRecipe(): RecipeDoc {
  return { title: '', intro: '', facts: [], ingredients: [], steps: [], notes: [] };
}

/** Nothing has been written yet — the editor is still waiting to be told the recipe. */
export function isBlankRecipe(doc: RecipeDoc): boolean {
  return !doc.title.trim() && !doc.intro.trim() && !doc.facts.length && !doc.ingredients.length && !doc.steps.length && !doc.notes.length;
}

/**
 * "For the syrup:" — a line that introduces the lines under it.
 *
 * A trailing colon is how people already write this down, in both languages, so
 * it needs no button and no explaining: type it and it is a heading. Kept short
 * on purpose, so that a real step which happens to end "…then add the following:"
 * stays a step.
 */
export function isHeadingLine(text: string): boolean {
  const s = text.trim();
  return s.length > 1 && s.length <= 60 && /[:：]$/.test(s);
}

/** A heading as it is shown — the colon did its job by being typed. */
export function headingLabel(text: string): string {
  return text.trim().replace(/\s*[:：]$/, '');
}

/**
 * Every line of the page, numbered.
 *
 * The title is always there, even empty, because it is the one line every recipe
 * must end up with and an empty line 1 is the invitation to fill it. The line
 * about the dish only exists once somebody has said something.
 */
export function recipeLines(doc: RecipeDoc): RecipeLine[] {
  const out: RecipeLine[] = [];
  let n = 0;
  out.push({ n: ++n, section: 'title', index: 0, text: doc.title, heading: false });
  if (doc.intro.trim()) out.push({ n: ++n, section: 'intro', index: 0, text: doc.intro, heading: false });
  doc.facts.forEach((text, index) => out.push({ n: ++n, section: 'facts', index, text, heading: false }));
  doc.ingredients.forEach((text, index) => out.push({ n: ++n, section: 'ingredients', index, text, heading: isHeadingLine(text) }));
  let step = 0;
  doc.steps.forEach((text, index) => {
    const heading = isHeadingLine(text);
    out.push({ n: ++n, section: 'steps', index, text, heading, step: heading ? undefined : ++step });
  });
  doc.notes.forEach((text, index) => out.push({ n: ++n, section: 'notes', index, text, heading: false }));
  return out;
}

/**
 * The page as the model reads it: the same numbers the person is looking at.
 *
 * Each line says what it is as well as where it is, so "line 14" and "step 2" and
 * "the second ingredient" all land on the same thing.
 */
export function numberedRecipe(doc: RecipeDoc): string {
  let ingredient = 0;
  return recipeLines(doc)
    .map((l) => {
      const kind =
        l.section === 'title' ? 'title'
        : l.section === 'intro' ? 'about'
        : l.section === 'facts' ? 'fact'
        : l.section === 'notes' ? 'note'
        : l.heading ? `${l.section === 'steps' ? 'step' : 'ingredient'} heading`
        : l.section === 'steps' ? `step ${l.step}`
        : `ingredient ${++ingredient}`;
      return `${l.n} | ${kind} | ${l.text || '(empty)'}`;
    })
    .join('\n');
}

/** A sub-heading and the lines under it, for the reading page. The first group may have no heading. */
export interface RecipeGroup {
  heading?: string;
  items: Array<{ text: string; index: number; step?: number }>;
}

export function groupLines(lines: string[]): RecipeGroup[] {
  const groups: RecipeGroup[] = [];
  let step = 0;
  lines.forEach((text, index) => {
    if (isHeadingLine(text)) {
      groups.push({ heading: headingLabel(text), items: [] });
      return;
    }
    if (!groups.length) groups.push({ items: [] });
    groups[groups.length - 1].items.push({ text, index, step: ++step });
  });
  return groups;
}

// ---------------------------------------------------------------------------
// Changing one line — by hand
// ---------------------------------------------------------------------------

/** One line of words: no line breaks, no runs of spaces, nothing absurdly long. */
export function tidyLine(text: string, max = MAX_LINE_CHARS): string {
  return text.replace(/\s+/g, ' ').trim().slice(0, max);
}

/** What was typed or pasted into one line, as the line(s) it really is. */
function typedLines(text: string): string[] {
  return text.split(/\r?\n/).map((l) => tidyLine(l)).filter(Boolean);
}

/**
 * Put new words on a line.
 *
 * Emptying a line takes it away (the title and the line about the dish are
 * emptied instead — there is always exactly one of each). Pasting several lines
 * into one makes several lines, in place, because that is plainly what was meant.
 */
export function setLine(doc: RecipeDoc, section: RecipeSection, index: number, text: string): RecipeDoc {
  if (section === 'title') return { ...doc, title: tidyLine(text, MAX_TITLE_CHARS) };
  if (section === 'intro') return { ...doc, intro: tidyLine(text) };
  const list = [...doc[section]];
  if (index < 0 || index >= list.length) return doc;
  list.splice(index, 1, ...typedLines(text));
  return { ...doc, [section]: list.slice(0, MAX_SECTION_LINES) };
}

/** A new line, after `after` — or at the end of its section when that is left out. */
export function addLine(doc: RecipeDoc, section: RecipeListSection | 'intro', text: string, after?: number): RecipeDoc {
  if (section === 'intro') return { ...doc, intro: tidyLine(text) };
  const lines = typedLines(text);
  if (!lines.length) return doc;
  const list = [...doc[section]];
  const at = after === undefined ? list.length : Math.min(list.length, Math.max(0, after + 1));
  list.splice(at, 0, ...lines);
  return { ...doc, [section]: list.slice(0, MAX_SECTION_LINES) };
}

export function removeLine(doc: RecipeDoc, section: RecipeSection, index: number): RecipeDoc {
  return setLine(doc, section, index, '');
}

// ---------------------------------------------------------------------------
// What a model (or a paste) handed back, made safe to keep
// ---------------------------------------------------------------------------

/** "- ", "• ", "* " at the start of a line: list furniture, not words. */
const BULLET = /^\s*(?:[-–—•*·▪◦‣]+)\s+/;
/** "1. ", "2) ", "Step 3:", "שלב 3 -" — the page numbers the steps itself. */
const STEP_MARK = /^\s*(?:(?:step|שלב)\s*\d{1,2}\s*[:.)\-–]?\s+|\d{1,2}[.)]\s+)/i;

function asLines(v: unknown, strip: RegExp[]): string[] {
  const raw: unknown[] = Array.isArray(v) ? v : typeof v === 'string' ? v.split(/\r?\n/) : [];
  const out: string[] = [];
  for (const item of raw) {
    // A model asked for strings sometimes answers with `{ text }` anyway.
    const s = typeof item === 'string' ? item : item && typeof item === 'object' && typeof (item as { text?: unknown }).text === 'string' ? (item as { text: string }).text : '';
    for (let line of s.split(/\r?\n/)) {
      for (const mark of strip) line = line.replace(mark, '');
      line = tidyLine(line);
      if (line) out.push(line);
    }
  }
  return out.slice(0, MAX_SECTION_LINES);
}

/**
 * Whatever came back, as a recipe that can be stored and drawn.
 *
 * Nothing here trusts its input: a model can return a number where a string was
 * asked for, a whole method in one string with line breaks in it, steps it has
 * numbered itself, or nothing at all. Every one of those becomes either clean
 * lines or an empty section — never an exception halfway through somebody's
 * grandmother's soup.
 */
export function normaliseRecipe(raw: unknown): RecipeDoc {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const str = (v: unknown, max: number) => (typeof v === 'string' ? tidyLine(v, max) : '');
  return {
    title: str(r.title, MAX_TITLE_CHARS).replace(/^#+\s*/, ''),
    intro: str(r.intro, MAX_LINE_CHARS),
    facts: asLines(r.facts, [BULLET]).slice(0, 8),
    ingredients: asLines(r.ingredients, [BULLET]),
    steps: asLines(r.steps, [BULLET, STEP_MARK]),
    notes: asLines(r.notes, [BULLET]),
  };
}

// ---------------------------------------------------------------------------
// Laying words out with no model at all
// ---------------------------------------------------------------------------

const HEAD_INGREDIENTS = /^(?:ingredients?|what you(?:'ll| will)? need|you(?:'ll| will) need|מצרכים|רכיבים|מרכיבים|חומרים|מה צריך)\s*[:：]?$/i;
const HEAD_STEPS = /^(?:method|steps?|directions?|instructions?|preparation|how to make(?: it)?|what to do|אופן ההכנה|אופן הכנה|הוראות הכנה|הוראות|הכנה|שלבים|שלבי ההכנה|מה עושים)\s*[:：]?$/i;
const HEAD_NOTES = /^(?:notes?|tips?|variations?|הערות|הערה|טיפים|טיפ)\s*[:：]?$/i;
/*
 * "Serves 8", "Prep time: 20 minutes", "מנות: 6". Strict on purpose — a number
 * straight after the word, or a colon — because "Cook the onions until soft"
 * also starts with a word off this list and is very much not a fact. (No `\b`
 * either: it does not know Hebrew letters are letters.)
 */
const FACT = /^(?:(?:serves|makes|yields?)\s+(?:about\s+|around\s+)?\d|(?:prep(?:aration)?|cook(?:ing)?|bak(?:e|ing)|total|rest(?:ing)?)(?:\s+time)?\s*[:：]|(?:time|oven|serves|makes|yields?)\s*[:：]|(?:מנות|כמות|תנור|זמן(?:\s+\S+)?)\s*[:：]|מספיק ל|\d+\s+מנות)/i;
/** Starts the way an ingredient does: a number, a fraction, a bullet. */
const STARTS_LIKE_INGREDIENT = /^(?:[-–—•*·▪◦‣]\s+|\d|[¼½¾⅓⅔⅛⅜⅝⅞])/;

/** A run of speech with no line breaks, cut where a person would have paused. */
function sentences(text: string): string[] {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (!flat) return [];
  const out: string[] = [];
  let current = '';
  for (let i = 0; i < flat.length; i++) {
    const ch = flat[i];
    current += ch;
    const end = ch === '.' || ch === '!' || ch === '?' || ch === '…';
    // Only before a space: "1.5 cups" is not the end of a sentence.
    if (end && (i + 1 === flat.length || flat[i + 1] === ' ')) {
      out.push(current.trim());
      current = '';
    }
  }
  if (current.trim()) out.push(current.trim());
  if (out.length > 1) return out;
  /*
   * A recogniser hands over speech with no punctuation at all, so the only
   * breaks left are the words people use to move on: "then", "after that",
   * "ואז", "אחר כך". Cutting there turns one unreadable paragraph into steps
   * somebody can at least tidy.
   */
  return flat
    .split(/\s+(?=(?:and then|then|after that|next|finally|ואז|אחר כך|לאחר מכן|אחרי זה|בסוף)\s)/i)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Typed, pasted or dictated words, laid out as a recipe **without changing any
 * of them**.
 *
 * This is what stands behind the assistant when there is none — no key, no
 * server, no signal — and what somebody falls back to when a write-up fails. It
 * does not tidy, reorder or interpret; it only decides which line goes under
 * which label, from the shape people already give a written recipe: a name at
 * the top, the word "Ingredients", the word "Method". Anything it is unsure of
 * becomes a step, because a step is the one place any sentence is at home.
 */
export function layOutRecipe(text: string): RecipeDoc {
  const doc = emptyRecipe();
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return doc;

  // One unbroken run — a dictation. There is no name in it to find, so none is invented.
  if (lines.length === 1) {
    doc.steps = sentences(lines[0]).map((s) => tidyLine(s)).filter(Boolean).slice(0, MAX_SECTION_LINES);
    return doc;
  }

  const labelOf = (l: string): RecipeListSection | null => (HEAD_INGREDIENTS.test(l) ? 'ingredients' : HEAD_STEPS.test(l) ? 'steps' : HEAD_NOTES.test(l) ? 'notes' : null);
  const labelled = lines.some((l) => labelOf(l) !== null);
  /** Which label we are under; null until the first one. */
  let under: RecipeListSection | null = null;
  /** In a recipe with no labels: once a sentence has gone by, the list of ingredients is over. */
  let seenSentence = false;

  for (const [i, line] of lines.entries()) {
    const label = labelOf(line);
    if (label) {
      under = label;
      continue;
    }
    if (i === 0) {
      doc.title = tidyLine(line.replace(/^#+\s*/, ''), MAX_TITLE_CHARS);
      continue;
    }
    const bare = tidyLine(line.replace(BULLET, ''));
    if (!bare) continue;
    const asStep = tidyLine(bare.replace(STEP_MARK, ''));

    if (under === 'steps') doc.steps.push(asStep);
    else if (under) doc[under].push(bare);
    // Above the first label — or in a recipe that has none.
    else if (FACT.test(bare) && bare.length <= 60) doc.facts.push(bare);
    else if (labelled) doc.intro = doc.intro ? tidyLine(`${doc.intro} ${bare}`) : bare;
    else {
      if (STEP_MARK.test(line) || bare.length > 70 || /[.!?]$/.test(bare)) seenSentence = true;
      if (!seenSentence && (STARTS_LIKE_INGREDIENT.test(line) || bare.length <= 48)) doc.ingredients.push(bare);
      else doc.steps.push(asStep);
    }
  }

  for (const s of LIST_SECTIONS) doc[s] = doc[s].slice(0, MAX_SECTION_LINES);
  return doc;
}

// ---------------------------------------------------------------------------
// What just changed
// ---------------------------------------------------------------------------

/**
 * The numbers of the lines that are new or different in `after`.
 *
 * When the assistant rewrites the page, this is what lets the changed lines light
 * up for a moment — so "did it do what I asked, and only that?" is answered by
 * looking rather than by re-reading the whole recipe. A line that merely moved is
 * not a change; a line that was taken away cannot be lit, and the assistant's own
 * sentence covers that.
 */
export function changedLines(before: RecipeDoc, after: RecipeDoc): number[] {
  const had = new Map<string, number>();
  for (const l of recipeLines(before)) {
    const key = `${l.section}:${l.text}`;
    had.set(key, (had.get(key) ?? 0) + 1);
  }
  const out: number[] = [];
  for (const l of recipeLines(after)) {
    const key = `${l.section}:${l.text}`;
    const left = had.get(key) ?? 0;
    if (left > 0) had.set(key, left - 1);
    else if (l.text.trim()) out.push(l.n);
  }
  return out;
}

export function sameRecipe(a: RecipeDoc, b: RecipeDoc): boolean {
  if (a.title !== b.title || a.intro !== b.intro) return false;
  return LIST_SECTIONS.every((s) => a[s].length === b[s].length && a[s].every((line, i) => line === b[s][i]));
}

// ---------------------------------------------------------------------------
// Ready to be shared?
// ---------------------------------------------------------------------------

/**
 * Why a recipe cannot go out yet, or null when it can.
 *
 * Two things and no more: it needs a name, so it can be found, and it needs
 * something to cook from. Everything else — how many it serves, how long it
 * takes, a line about the dish — is welcome and never required, because the
 * recipes most worth keeping are exactly the ones nobody ever measured.
 */
export function publishProblem(doc: RecipeDoc): 'title' | 'body' | null {
  if (!doc.title.trim()) return 'title';
  const real = (lines: string[]) => lines.some((l) => !isHeadingLine(l));
  if (!real(doc.ingredients) && !real(doc.steps)) return 'body';
  return null;
}

// ---------------------------------------------------------------------------
// Whose it is, and who sees it
// ---------------------------------------------------------------------------

/**
 * Who may change a recipe, and why — the same three answers a story gives
 * (domain/storyEditing.ts), with one addition.
 *
 *   'own'   — you wrote it down, **or it is under your name**: a recipe hangs on
 *             a person's circle, so that person (and anyone who looks after their
 *             circle) can always correct or take down what is said to be theirs.
 *   'admin' — only because Admin mode is on; painted in the admin colour.
 *   'none'  — everybody else reads it.
 */
export type RecipeEditRight = 'own' | 'admin' | 'none';

export function recipeEditRight(actor: Actor, recipe: Pick<Recipe, 'creatorAccountId' | 'personId'>): RecipeEditRight {
  if (recipe.creatorAccountId === actor.session.accountId) return 'own';
  if (managesPerson(actor, recipe.personId)) return 'own';
  return actor.session.role === 'admin' ? 'admin' : 'none';
}

/** The page somebody editing works on: their unpublished changes if there are any, else what is out. */
export function workingRecipe(recipe: Pick<Recipe, 'doc' | 'draft'>): RecipeDoc {
  return recipe.draft ?? recipe.doc;
}

/**
 * The recipes that belong on one person's circle, newest first.
 *
 * Two ways to belong, as with a story: it is *their* recipe, or they are the one
 * who wrote it down for somebody else — Sarah typing out Savta Miriam's fish has
 * given the family something too, and it is findable under both of them.
 * `accountIds` are the accounts joined to this circle.
 */
export function recipesOnCircle(recipes: Recipe[], personId: UUID, accountIds: ReadonlySet<UUID>): Recipe[] {
  return recipes
    .filter((r) => r.status !== 'archived' && (r.personId === personId || accountIds.has(r.creatorAccountId)))
    .sort((a, b) => recipeWhen(b).localeCompare(recipeWhen(a)));
}

/**
 * The language a recipe is written in, read off its own words.
 *
 * Not the language of the screen: a grandmother dictating in Hebrew to a
 * grandchild whose app is in English has told a Hebrew recipe, and the page has
 * to run right-to-left for it whoever is looking. Hebrew letters against Latin
 * ones, counted; a recipe with neither keeps whatever it was.
 */
export function recipeLanguage(doc: RecipeDoc, fallback: Lang): Lang {
  const all = [doc.title, doc.intro, ...doc.facts, ...doc.ingredients, ...doc.steps, ...doc.notes].join(' ');
  const hebrew = (all.match(/\p{Script=Hebrew}/gu) ?? []).length;
  const latin = (all.match(/[A-Za-z]/g) ?? []).length;
  if (!hebrew && !latin) return fallback;
  return hebrew >= latin ? 'he' : 'en';
}

/** When a recipe arrived, for ordering and for how new it still looks. */
export function recipeWhen(r: Pick<Recipe, 'publishedAt' | 'updatedAt'>): string {
  return r.publishedAt ?? r.updatedAt;
}

/* ---------- its pictures ---------- */

/**
 * The pictures the family has added to one recipe, **oldest first**.
 *
 * They are ordinary Moments pointing at the recipe (`addedTo`), read off the
 * list the app already has — nothing about them is stored on the recipe. The
 * order is the one thing here that carries meaning: the first picture anybody
 * added is the one that stands for the recipe until somebody chooses another,
 * so "first" has to mean the same thing everywhere, and new pictures arrive at
 * the end of the row, next to the button that added them.
 *
 * Stills only. A recipe's pictures are what goes on its card and across the top
 * of its page, and a clip or a voice note can be neither.
 */
export function recipePictures(moments: FamilyPhoto[], recipeId: UUID): FamilyPhoto[] {
  // Its own few, read out of the pile (see `addedTo`): the shelf asks this once for every recipe on it.
  return addedTo(moments, { kind: 'recipe', id: recipeId })
    .filter((m) => !!m.storageKey && !m.removed && !!m.mimeType?.startsWith('image/'))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
}

/**
 * The picture that stands for a recipe, or nothing when it has none.
 *
 * The one somebody chose, as long as it is still there to be shown — a picture
 * an administrator has taken down quietly stops being the cover rather than
 * leaving a hole — and otherwise the first one the family added. Pass either
 * the family's Moments or a list already narrowed by `recipePictures`.
 */
export function recipeCover(recipe: Pick<Recipe, 'id' | 'coverPhotoId'>, moments: FamilyPhoto[]): FamilyPhoto | undefined {
  const pictures = recipePictures(moments, recipe.id);
  return pictures.find((p) => p.id === recipe.coverPhotoId) ?? pictures[0];
}
